import * as React from "react";
import { Link, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import CclInfoTableEditDatePicker from "./cclInfoTableEditComponents/cclInfoTableEditDatePicker";
import CclInfoTableEditSelect from "./cclInfoTableEditComponents/cclInfoTableEditSelect";
import CclInfoTableEditText from "./cclInfoTableEditComponents/cclInfoTableEditText";
import CclInfoTableEditCheckbox from "./cclInfoTableEditComponents/cclInfoTableEditCheckbox";
import CclInfoTableEditNotes from "./cclInfoTableEditComponents/cclInfoTableNotes";
import CclInfoTableEditSwitch from "./cclInfoTableEditComponents/cclInfoTableEditSwitch";

export enum eInfoTableValueFieldEditType {
    Text = 0,
    DatePicker = 1,
    Select = 3,
    Checkbox = 4,
    DateTimeText = 5,
    Link = 6,
    TextInput = 7,
    YesNoLabel = 8,
    NotesInput = 9,
    Switch = 10,
}

export type CclInfoTableValueFieldProps = {
    value: string;
    editFieldType: eInfoTableValueFieldEditType;
    url: string | null;
    hoverText: string | null;
    checked: boolean | null;
    id: string;
    selectOptions: { val: string; option: string }[] | null;
    label?: string;
    required?: boolean;
    placeholder?: string;
    onChange?: (name: string, value: any, required: boolean) => void;
    editable?: boolean;
    empty?: boolean;
    readonly?: boolean;
    sx?: SxProps<Theme>;
    minDate?: Date;
    maxDate?: Date;
};

const CclInfoTableValueField: React.FC<CclInfoTableValueFieldProps> = (props) => {
    const [required, setRequired] = React.useState<boolean>(false);
    const readOnly = !props?.editable || (props?.readonly ?? false);

    const handleChange = (value: any, required: boolean) => {
        if (props.onChange) props.onChange(props.id, value, required);
    };

    React.useEffect(() => {
        if (props.required) {
            setRequired(true);
        } else {
            setRequired(false);
        }
    }, [props.required]);

    return (() => {
        switch (props.editFieldType) {
            case eInfoTableValueFieldEditType.Text:
                return props.value.length > 50 ? (
                    <Tooltip title={props.value} arrow>
                        <Typography
                            sx={{ ...props?.sx, fontSize: 14, width: 1, overflow: "hidden" }}
                        >
                            {props.value}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography sx={{ ...props?.sx, fontSize: 14, width: 1, overflow: "hidden" }}>
                        {props.value}
                    </Typography>
                );
            case eInfoTableValueFieldEditType.DatePicker:
                return (
                    <CclInfoTableEditDatePicker
                        required={required}
                        value={props.value}
                        onChange={handleChange}
                        readonly={readOnly}
                        minDate={props.minDate}
                        maxDate={props.maxDate}
                    />
                );
            case eInfoTableValueFieldEditType.Select:
                return (
                    <CclInfoTableEditSelect
                        required={required}
                        value={props.value}
                        onChange={handleChange}
                        options={props.selectOptions}
                        readonly={readOnly}
                        empty={props.empty}
                        placeholder={props.placeholder}
                    />
                );
            case eInfoTableValueFieldEditType.Checkbox:
                return (
                    <CclInfoTableEditCheckbox
                        required={required}
                        value={props.checked}
                        onChange={handleChange}
                        readonly={readOnly}
                    />
                );
            case eInfoTableValueFieldEditType.Link:
                let v = props.value;
                if (v.length > 50) {
                    v = v.substring(0, 50) + "...";
                }
                return props.value == null || props.value === "" ? (
                    <Typography>{""}</Typography>
                ) : props.value.length > 50 ? (
                    <Tooltip title={props.value} arrow>
                        <Link
                            href={props.url as string}
                            sx={{ ...props.sx, fontSize: 14, width: 1, overflow: "hidden" }}
                        >
                            {v}
                        </Link>
                    </Tooltip>
                ) : (
                    <Link href={props.url as string}>{props.value}</Link>
                );
            case eInfoTableValueFieldEditType.TextInput:
                return (
                    <CclInfoTableEditText
                        required={required}
                        value={props.value}
                        onChange={handleChange}
                        readonly={readOnly}
                        sx={props?.sx ? { ...props.sx } : {}}
                    />
                );
            case eInfoTableValueFieldEditType.YesNoLabel:
                return <Typography sx={props.sx}>{props.value ? "Yes" : "No"}</Typography>;
            case eInfoTableValueFieldEditType.NotesInput:
                return (
                    <CclInfoTableEditNotes
                        label={props.label ?? ""}
                        value={props.value}
                        onChange={handleChange}
                        required={required}
                        readonly={readOnly}
                    />
                );
            case eInfoTableValueFieldEditType.Switch:
                return (
                    <CclInfoTableEditSwitch
                        required={required}
                        value={props.checked}
                        onChange={handleChange}
                        readonly={readOnly}
                    />
                );
            default:
                let value = new Date(props.value).toLocaleDateString();
                return <Typography>{value ? value : ""}</Typography>;
        }
    })();
};

export default CclInfoTableValueField;
